import React, { useEffect, useState } from 'react'
import { Grid, Box, Button, Typography, CircularProgress, makeStyles } from '@material-ui/core'
import { Formik, Form } from 'formik'
import CustomTextField from 'components/Form/CustomTextField'
import CustomBox from 'components/Form/CustomBox'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import ProgressButton from 'components/ProgressButton'
import { adminsFormSchema, updatePasswordSchema } from 'schemas/admins'
import { createUser, fetchUserByUserId, updateUserPasswordByUserId } from 'store/Admins'
import { useDispatch } from 'react-redux'
import { removeLeadingAndTrailingSpaces } from 'utils'
import conferenceSlice, { fetchConferenceById } from 'store/conference'
import ViewHeader from 'components/ViewHeader'
import ActionBar from 'components/ActionBar'
import { toast } from 'react-toastify'

const AdminsForm = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const location = useLocation()
  const confId = new URLSearchParams(location.search).get('conferenceId')
  const history = useHistory()
  const { adminId } = useParams()
  const [loading, setLoading] = useState(false)
  const {
    conferences,
    conference,
    inProgress: confProgress
  } = useSelector((state) => state.conference)
  const { admin, inProgress, isCreatedOrUpdated } = useSelector((state) => state.admins)

  const initialValues = {
    email: '',
    fullName: '',
    password: ''
  }

  const getConferenceName = (confId) => {
    const singleConf = conferences?.find((c) => c?.id === confId)
    return singleConf?.confName
  }

  useEffect(() => {
    if (confId) {
      dispatch(fetchConferenceById(confId))
    }
    return () => {
      dispatch(conferenceSlice.actions.setConference({}))
    }
  }, [confId])

  useEffect(() => {
    if (adminId) dispatch(fetchUserByUserId(adminId))
  }, [dispatch, adminId])

  useEffect(() => {
    if (isCreatedOrUpdated) {
      history.goBack()
    }
  }, [isCreatedOrUpdated])

  const submitHandler = async (values) => {
    setLoading(true)
    const payload = {
      fullName: removeLeadingAndTrailingSpaces(values.fullName),
      email: values.email,
      password: removeLeadingAndTrailingSpaces(values.password),
      confId: confId
    }
    try {
      await createUser(payload)
      history.goBack()
    } catch (error) {
      toast.error(error.response.data.error.message)
    }
    setLoading(false)
  }

  if (confId && confProgress)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: '1.5rem' }}>
        <CircularProgress />
      </Box>
    )

  return (
    <CustomBox>
      <ViewHeader title={`${adminId ? 'Edit' : 'Create'} Conference Admin`}>
        <ActionBar actionText="Back" onAction={() => history.goBack()} />
      </ViewHeader>
      {confId && (
        <Box p={1} border={1} sx={{ mb: '2rem' }}>
          <Box p={1} style={{ backgroundColor: '#ECECEC' }}>
            <Box className={classes.singleInfo}>
              <Typography className={classes.label}>Conference Name: </Typography>
              <Typography>{conference?.confName}</Typography>
            </Box>
          </Box>
        </Box>
      )}

      {adminId && inProgress ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: '1.5rem' }}>
          <CircularProgress />
        </Box>
      ) : adminId && !inProgress ? (
        <>
          <Box p={1} border={1} sx={{ mb: '2rem' }}>
            <Box p={1} style={{ backgroundColor: '#ECECEC' }}>
              <Box className={classes.singleInfo}>
                <Typography className={classes.label}>Admin Email: </Typography>
                <Typography>{admin.email}</Typography>
              </Box>
              <Box className={classes.singleInfo}>
                <Typography className={classes.label}>Admin Full Name: </Typography>
                <Typography>{admin.fullName}</Typography>
              </Box>
              <Box className={classes.singleInfo}>
                <Typography className={classes.label}>Conference: </Typography>
                <Typography>{getConferenceName(admin?.conferenceId)}</Typography>
              </Box>
            </Box>
          </Box>

          <Formik
            initialValues={{ fullName: admin?.fullName ?? '', newPassword: '' }}
            validationSchema={updatePasswordSchema}
            onSubmit={async (values) => {
              setLoading(true)
              const requestData = {
                uid: adminId,
                fullName: removeLeadingAndTrailingSpaces(values.fullName),
                newPassword: values.newPassword
              }
              await dispatch(updateUserPasswordByUserId(requestData))
              setLoading(false)
              history.goBack()
            }}>
            {() => (
              <Form autoComplete="off" style={{ marginBottom: '1rem', marginTop: '0.5rem' }}>
                <Grid container spacing={3} alignItems="center" justifyContent="space-between">
                  <Grid item xs={12} md={4}>
                    <CustomTextField
                      label="Admin Full Name"
                      name="fullName"
                      type="text"
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <CustomTextField
                      label="New Password"
                      name="newPassword"
                      type="text"
                      autoComplete="off"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box className={classes.buttonBox}>
                      <Button size="large" disabled={loading} onClick={() => history.goBack()}>
                        Cancel
                      </Button>
                      <ProgressButton
                        type="submit"
                        label="Update"
                        inProgress={loading}
                        disabled={loading}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={adminsFormSchema}
          onSubmit={submitHandler}>
          {() => (
            <Form autoComplete="off">
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <CustomTextField
                    label="Admin Full Name"
                    name="fullName"
                    type="text"
                    autoComplete="off"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField label="Email" name="email" autoComplete="off" />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField
                    label="Password"
                    name="password"
                    type="text"
                    autoComplete="off"
                  />
                </Grid>
              </Grid>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '0.75rem',
                  mt: '1rem',
                  justifyContent: 'flex-end'
                }}>
                <Grid item>
                  <Button size="large" disabled={loading} onClick={() => history.goBack()}>
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  <ProgressButton
                    type="submit"
                    label="Create"
                    inProgress={loading}
                    disabled={loading}
                  />
                </Grid>
              </Box>
            </Form>
          )}
        </Formik>
      )}
    </CustomBox>
  )
}
export default AdminsForm

const useStyles = makeStyles({
  headBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem'
  },
  confName: {
    marginLeft: '3px'
  },
  singleInfo: {
    display: 'flex',
    gap: '2rem'
  },
  label: {
    minWidth: '125px',
    fontSize: '15px',
    fontWeight: '600'
  },
  value: {
    fontSize: '14px',
    marginRight: '1rem'
  },
  buttonBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    gap: '0.75rem'
  }
})
